import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import { useEditor, EditorContent, Editor, JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";
import { Node, mergeAttributes, Extension } from '@tiptap/core';


export interface ReportEditorRef {
  getHTML: () => string;
  getJSON: () => object;
  getEditor?: () => Editor | null;
}

// ✅ Estensione Footnote — nota a piè di pagina come NODO inline atomico.
// Il BE emette la nota come `{type:"text", text:"", attrs:{class:"footnote",
// footnoteText:"…"}}`: un nodo di testo NON puo' portare attrs in ProseMirror,
// quindi il testo della nota andrebbe perso. Qui la modelliamo come nodo inline
// atomico con l'attributo `footnoteText`; il JSON in ingresso viene convertito
// da `sanitizeJSON` (text -> footnote). La numerazione [n] progressiva e' resa
// via CSS counter (vedi customStyles), il testo compare come tooltip (title).
const Footnote = Node.create({
  name: 'footnote',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      footnoteText: {
        default: '',
        parseHTML: el => el.getAttribute('data-footnote-text') ?? el.getAttribute('title') ?? '',
        renderHTML: attrs => (attrs.footnoteText ? { 'data-footnote-text': attrs.footnoteText } : {}),
      },
      // Testo manuale della nota (in rilascio lato BE). Gestito identico a
      // footnoteText: stringa preservata end-to-end via data-footnote-manual.
      footnoteManual: {
        default: '',
        parseHTML: el => el.getAttribute('data-footnote-manual') ?? '',
        renderHTML: attrs => (attrs.footnoteManual ? { 'data-footnote-manual': attrs.footnoteManual } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'sup[data-footnote]' }]
  },

  renderHTML({ HTMLAttributes, node }) {
    // Entrambi i testi possono coesistere: nel tooltip li mostro tutti e due
    // (quando presenti), mentre restano preservati separatamente nei data-attr.
    // NB: footnoteManual puo' arrivare dal BE come flag booleano (true) invece
    // che come testo -> considero SOLO i valori stringa non vuoti, altrimenti
    // verrebbe stampato il letterale "true".
    const tooltip = [node.attrs.footnoteText, node.attrs.footnoteManual]
      .filter(v => typeof v === 'string' && v.trim() !== '')
      .join('\n\n');
    return ['sup', mergeAttributes(HTMLAttributes, {
      'data-footnote': '',
      class: 'footnote',
      title: tooltip,
    })]
  },
});

// ✅ Conversione INVERSA per il salvataggio: il nodo custom `footnote` esiste
// solo nello schema TipTap del frontend. Il BE (e in particolare l'export DOCX)
// conosce SOLO il formato originale `{type:"text", text:"", attrs:{class:"footnote",
// footnoteText, footnoteManual}}`. Senza questa reifica, dopo una modifica via
// editor il JSON salvato contiene `{type:"footnote"}` che il generatore Word non
// riconosce -> le note spariscono dal documento scaricato.
// footnoteManual viene preservato COM'E' (puo' essere stringa o flag booleano
// `true` lato BE); footnoteText di default stringa vuota.
const restoreBackendFootnotes = (node: any): any => {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map(restoreBackendFootnotes);

  if (node.type === 'footnote') {
    return {
      type: 'text',
      text: '',
      attrs: {
        class: 'footnote',
        footnoteText:   node.attrs?.footnoteText ?? '',
        footnoteManual: node.attrs?.footnoteManual ?? '',
      },
    };
  }

  if (Array.isArray(node.content)) {
    return { ...node, content: node.content.map(restoreBackendFootnotes) };
  }
  return node;
};

// ✅ Preserva l'attributo `class` sui paragrafi. Il BE inserisce dopo la
// copertina un placeholder `{type:"paragraph", attrs:{class:"table-of-content"}}`
// che marca il punto in cui, in fase di export DOCX, va generato il sommario
// (indice). L'estensione Paragraph di TipTap NON dichiara `class`, quindi
// ProseMirror lo scartava al caricamento: al salvataggio il placeholder tornava
// come `<p>` anonimo e il BE, non trovandolo piu', posizionava il sommario in
// cima (prima della copertina). Dichiarando qui l'attributo, la classe fa il
// round-trip end-to-end (JSON e HTML) e il placeholder resta al suo posto.
const PreserveParagraphClass = Extension.create({
  name: 'preserveParagraphClass',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          class: {
            default: null,
            parseHTML: el => el.getAttribute('class'),
            renderHTML: attrs => (attrs.class ? { class: attrs.class } : {}),
          },
        },
      },
    ];
  },
});

/* ---------------- Note a piè di pagina ---------------- */
// Elenco delle note in fondo alla pagina: il marker inline mostra solo `[n]`,
// qui rendiamo il testo effettivo (footnoteText / footnoteManual) numerato in
// ordine di apparizione, coerente col contatore CSS.
const FootnotesList = ({ notes }: { notes: string[] }) => {
  if (!notes.length) return null;
  return (
    <div className="mt-8 pt-4 border-t border-gray-300 text-[11px] leading-snug text-gray-700">
      <ol className="list-none p-0 m-0 space-y-1">
        {notes.map((note, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-semibold text-[#2563eb] flex-shrink-0">[{i + 1}]</span>
            <span className="text-justify">{note}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ---------------- Toolbar ---------------- */
const MenuBar = ({ editor }: { editor: Editor | null }) => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => setVersion((v) => v + 1);
    editor.on("transaction", handler);
    editor.on("selectionUpdate", handler);
    editor.on("update", handler);
    return () => {
      editor.off("transaction", handler);
      editor.off("selectionUpdate", handler);
      editor.off("update", handler);
    };
  }, [editor]);

  if (!editor) return null;

  const Button = ({
    onClick,
    active,
    children,
    title,
  }: {
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
    title?: string;
  }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`px-2 py-1 rounded-md text-sm font-medium transition-colors border
        ${active
          ? "bg-[#374151] text-white border-[#374151]"
          : "bg-white text-[#374151] border-[#374151] hover:bg-gray-100"
        }`}
    >
      {children}
    </button>
  );

  const applyColor = (color: string) => {
    editor.chain().focus().setColor(color).run();
    const textStyleMark = (editor.schema as any).marks?.textStyle;
    if (textStyleMark) {
      const mark = textStyleMark.create({ color });
      const tr = editor.state.tr.setStoredMarks([mark]);
      editor.view.dispatch(tr);
      editor.view.focus();
    }
  };

  const unsetColor = () => {
    editor.chain().focus().unsetColor().run();
    editor.view.dispatch(editor.state.tr.setStoredMarks([]));
    editor.view.focus();
  };

  const insertImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        editor.chain().focus().setImage({ src: base64 }).run();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const addRowAfter = () => editor.chain().focus().addRowAfter().run();
  const deleteRow = () => editor.chain().focus().deleteRow().run();
  const addColumnAfter = () => editor.chain().focus().addColumnAfter().run();
  const deleteColumn = () => editor.chain().focus().deleteColumn().run();
  const deleteTable = () => editor.chain().focus().deleteTable().run();

  const currentTextStyleAttrs = editor.getAttributes
    ? editor.getAttributes("textStyle")
    : {};
  const currentColor = currentTextStyleAttrs?.color ?? null;

  return (
    <div className="flex flex-wrap gap-2 mb-4 border-b pb-2 border-gray-300">
      <div className="flex gap-2 items-center">
        <Button onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Grassetto">B</Button>
        <Button onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Corsivo">I</Button>
        <Button onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Sottolineato">U</Button>
        <Button onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Barrato">S̶</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragrafo">P</Button>
        <Button onClick={() => editor.chain().focus().setNode("heading", { level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Titolo H1">H1</Button>
        <Button onClick={() => editor.chain().focus().setNode("heading", { level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titolo H2">H2</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Elenco puntato">• Lista</Button>
        <Button onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Elenco numerato">1. Lista</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={() => applyColor("#000000")} active={currentColor === "#000000"} title="Nero">Nero</Button>
        <Button onClick={() => applyColor("#DC2626")} active={currentColor === "#DC2626"} title="Rosso">Rosso</Button>
        <Button onClick={() => applyColor("#2563EB")} active={currentColor === "#2563EB"} title="Blu">Blu</Button>
        <Button onClick={unsetColor} title="Rimuovi colore">✕</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={() => editor.chain().focus().setLink({ href: "https://example.com" }).run()} active={editor.isActive("link")} title="Aggiungi link">Link</Button>
        <Button onClick={() => editor.chain().focus().unsetLink().run()} title="Rimuovi link">Rimuovi Link</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={insertImage} title="Inserisci immagine">Immagine</Button>
        <Button onClick={insertTable} title="Inserisci tabella">Tabella</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={addRowAfter} title="Aggiungi riga sotto">+R</Button>
        <Button onClick={deleteRow} title="Elimina riga">-R</Button>
        <Button onClick={addColumnAfter} title="Aggiungi colonna">+C</Button>
        <Button onClick={deleteColumn} title="Elimina colonna">-C</Button>
        <Button onClick={deleteTable} title="Elimina tabella">🗑️</Button>
      </div>

      <div className="flex gap-2 items-center ml-4 pl-4 border-l first:border-l-0 first:ml-0 first:pl-0">
        <Button onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Allinea a sinistra">⬅</Button>
        <Button onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Allinea al centro">⬌</Button>
        <Button onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Allinea a destra">➡</Button>
        <Button onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Giustifica">☰</Button>
      </div>
    </div>
  );
};

/* ---------------------- Editor principale ---------------------- */
const ReportEditor = forwardRef<ReportEditorRef, { initialJSON?: JSONContent; editable?: boolean }>(
  ({ initialJSON, editable = false }, ref) => {

    // ✅ Funzione per sanitizzare il JSON rimuovendo nodi di testo vuoti
    const sanitizeJSON = (content: any): any => {
      if (!content) return content;

      if (content.type === 'text') {
        // Il BE serializza le note a piè di pagina come nodo `text` (text vuoto)
        // con attrs.class="footnote" + footnoteText. In ProseMirror i text node
        // non possono avere attrs, quindi lo convertiamo in un vero nodo inline
        // `footnote`; senza questo verrebbe scartato come testo vuoto poco sotto.
        if (content.attrs && (content.attrs.footnoteText != null || content.attrs.class === 'footnote')) {
          return {
            type: 'footnote',
            attrs: {
              footnoteText:   content.attrs.footnoteText ?? '',
              footnoteManual: content.attrs.footnoteManual ?? '',
            },
          };
        }
        // Se è un nodo di testo vuoto, ritorna null per rimuoverlo
        if (!content.text || content.text.trim() === '') {
          return null;
        }
        return content;
      }

      if (content.content && Array.isArray(content.content)) {
        // Sanitizza ricorsivamente il contenuto e filtra i null
        content.content = content.content
          .map((child: any) => sanitizeJSON(child))
          .filter((child: any) => child !== null);
      }

      return content;
    };

    // ✅ Sanitizza il JSON prima di passarlo all'editor
    const sanitizedJSON = initialJSON ? sanitizeJSON(JSON.parse(JSON.stringify(initialJSON))) : { type: "doc", content: [] };

    // ✅ Raccoglie le note a piè di pagina in ordine di apparizione nel
    // documento (stesso ordine del contatore CSS `[n]`). Il marker inline
    // mostra solo `[n]` + tooltip: il testo effettivo di footnoteText /
    // footnoteManual va reso visibile in fondo alla pagina.
    const collectFootnotes = (node: any, acc: string[] = []): string[] => {
      if (!node) return acc;
      if (node.type === 'footnote') {
        // footnoteManual puo' essere un flag booleano (true) e non testo:
        // includo solo stringhe non vuote per non stampare il letterale "true".
        const text = [node.attrs?.footnoteText, node.attrs?.footnoteManual]
          .filter(v => typeof v === 'string' && v.trim() !== '')
          .join(' — ');
        acc.push(text);
        return acc;
      }
      if (Array.isArray(node.content)) {
        node.content.forEach((child: any) => collectFootnotes(child, acc));
      }
      return acc;
    };
    const footnotes = collectFootnotes(sanitizedJSON);

    const editor = useEditor({
      editable,
      extensions: [
        StarterKit.configure({
          // Disabilita le estensioni gia' caricate separatamente per evitare
          // warning "Duplicate extension names found".
          link: false,
          underline: false,
        }),
        Underline,
        Link,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        Image,
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        PreserveParagraphClass, // ✅ Preserva class="table-of-content" (placeholder sommario)
        Footnote, // ✅ Aggiunta estensione Footnote
      ],
      content: sanitizedJSON,
    });

    useEffect(() => {
      if (editor) {
        editor.setEditable(editable);
      }
    }, [editor, editable]);

    useImperativeHandle(ref, () => ({
      getHTML: () => editor?.getHTML() ?? "",
      // Riconverte i nodi `footnote` nel formato text/class:"footnote" atteso
      // dal BE, così le note restano visibili anche nel Word scaricato dopo
      // una modifica via editor.
      getJSON: () => restoreBackendFootnotes(editor?.getJSON() ?? {}),
    }));

    const customStyles = `
      .prose p:empty::before {
        content: "\\00A0";
        display: block;
        line-height: 1.5;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        text-align: justify;
      }

      th, td {
        padding: 4px 6px;
        border: 1px solid #ccc;
        font-size: 12px;
        word-break: break-word;
      }

      th { background-color: #f3f4f6; font-weight: 600; }

      table, img { page-break-inside: avoid; }

      img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 12px auto;
      }

      ul, ol {
        padding-left: 1.5em;
        margin: 0 0 6px 0;
      }

      ul li, ol li {
        margin: 0 0 6px 0;
        line-height: 1.5;
      }

      ul {
        list-style-type: disc;
      }

      ol {
        list-style-type: decimal;
      }

      .editable-editor ol {
        counter-reset: item;
        padding-left: 1.5em;
        margin: 0 0 6px 0;
      }

      .editable-editor ol li {
        display: flex;
        align-items: flex-start;
        gap: 0.5em;
        margin: 0 0 6px 0;
        line-height: 1.5;
        width: 100%;
        counter-increment: item;
      }

      .editable-editor ol li::before {
        content: counter(item) ".";
        font-weight: bold;
        flex-shrink: 0;
        margin-right: 0.3em;
      }

      /* Note a piè di pagina: numerazione [n] progressiva via CSS counter
       * (in ordine di apparizione), testo della nota nel tooltip (title). */
      .prose { counter-reset: footnote; }

      sup.footnote {
        counter-increment: footnote;
        cursor: help;
        color: #2563eb;
        font-weight: 600;
      }

      sup.footnote::before {
        content: "[" counter(footnote) "]";
      }
    `;

    return (
      <>
        <style>{customStyles}</style>

        <div className="border rounded-lg bg-white shadow flex flex-col relative">
          {editable && (
            <div className="sticky top-0 z-20 bg-white border-b p-4 shadow-sm">
              <MenuBar editor={editor} />
            </div>
          )}

          {editable ? (
            <div className="max-h-[85vh] overflow-y-auto bg-gray-50 p-8 editable-editor">
              <div className="pb-40">
                <EditorContent
                  editor={editor}
                  className="prose max-w-none text-[13px] leading-relaxed
                         [&_ul]:list-disc [&_ol]:list-decimal [&_li]:ml-5
                         [&_ul]:pl-6 [&_ol]:pl-6
                         [&_table]:border [&_table]:border-gray-400 [&_table]:border-collapse
                         [&_th]:border [&_td]:border [&_th]:border-gray-400 [&_td]:border-gray-300
                         [&_th]:bg-gray-100 [&_td]:p-1 [&_th]:p-1"
                />
                <FootnotesList notes={footnotes} />
              </div>
            </div>
          ) : (
            <div className="max-h-[75vh] overflow-y-auto bg-gray-100 p-12">
              <div className="bg-white shadow-md border border-gray-300 p-12 prose max-w-none text-[13px] leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: editor?.getHTML() || "" }} />
                <FootnotesList notes={footnotes} />
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
);

export default ReportEditor;